<template>
  <div v-if="!screenshotMode" ref="controlContainer" class="map-kit-control-container">
    <div ref="layersMenuAnchor" class="map-kit-interaction-buttons">
      <Button
        v-tooltip.left="$t('toolbar.layers')"
        icon="fas fa-layer-group"
        :severity="showLayersMenu ? 'primary' : 'contrast'"
        :class="{ 'toolbar-item-selected': showLayersMenu }"
        size="small"
        text
        aria-haspopup="dialog"
        :aria-expanded="showLayersMenu"
        :aria-label="$t('toolbar.layers')"
        @click="toggleLayersMenu"
      />
      <Button
        v-tooltip.left="'Take screenshot'"
        icon="fas fa-camera"
        severity="contrast"
        size="small"
        text
        aria-label="Screenshot"
        @click="onScreenshotClick"
      />
      <Button
        v-tooltip.left="'Measure'"
        icon="fas fa-ruler"
        :severity="showMeasurementMenu ? 'primary' : 'contrast'"
        :class="{ 'toolbar-item-selected': showMeasurementMenu }"
        size="small"
        text
        aria-label="Measurement"
        @click="showMeasurementMenu = !showMeasurementMenu"
      />
      <Button
        v-tooltip.left="'Fullscreen'"
        :icon="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"
        :severity="isFullscreen ? 'primary' : 'contrast'"
        :class="{ 'toolbar-item-selected': isFullscreen }"
        size="small"
        text
        aria-label="Toggle fullscreen"
        @click="toggleFullscreen"
      />

      <div
        v-if="showLayersMenu"
        ref="layersDrawer"
        class="map-kit-layer-drawer"
        :class="{ 'map-kit-layer-drawer--compact': isLayersDrawerCompact }"
        :style="layersDrawerStyle"
        role="dialog"
        tabindex="0"
        :aria-label="$t('toolbar.layers')"
        @click.stop
      >
        <div class="map-kit-layer-drawer-header">
          <h3 class="map-kit-layer-drawer-title">{{ $t('toolbar.layers') }}</h3>
          <button
            class="map-kit-layer-drawer-close"
            type="button"
            aria-label="Close layers"
            @click="showLayersMenu = false"
          >
            <FontAwesomeIcon icon="fas fa-xmark" />
          </button>
        </div>

        <div class="map-kit-layer-drawer-body">
          <div class="map-kit-panel-section">
            <h4 class="map-kit-panel-section-title">{{ $t('layers.baseMaps') }}</h4>
            <MapBasemapSelector />
          </div>

          <div class="map-kit-panel-section">
            <h4 class="map-kit-panel-section-title">{{ $t('layers.cloudLayers') }}</h4>

            <div class="map-kit-layer-control">
              <div class="map-kit-filter-checkbox-row">
                <Checkbox
                  v-model="eumetsatTrueRgbVisible"
                  input-id="layer-eumetsat-truergb"
                  :binary="true"
                />
                <label for="layer-eumetsat-truergb">{{ $t('layers.eumetsatTrueRgb') }}</label>
              </div>
              <div v-if="eumetsatTrueRgbVisible" class="map-kit-layer-opacity">
                <label class="map-kit-layer-opacity-label">{{ $t('layers.opacity') }}</label>
                <Slider
                  v-model="eumetsatTrueRgbOpacityPct"
                  :min="0"
                  :max="100"
                  :step="5"
                  class="map-kit-layer-opacity-slider"
                />
                <span class="map-kit-layer-opacity-value">{{ eumetsatTrueRgbOpacityPct }}%</span>
              </div>
            </div>

            <div class="map-kit-layer-control">
              <div class="map-kit-filter-checkbox-row">
                <Checkbox
                  v-model="eumetsatInfraredVisible"
                  input-id="layer-eumetsat-ir"
                  :binary="true"
                />
                <label for="layer-eumetsat-ir">{{ $t('layers.eumetsatIr') }}</label>
              </div>
              <div v-if="eumetsatInfraredVisible" class="map-kit-layer-opacity">
                <label class="map-kit-layer-opacity-label">{{ $t('layers.opacity') }}</label>
                <Slider
                  v-model="eumetsatInfraredOpacityPct"
                  :min="0"
                  :max="100"
                  :step="5"
                  class="map-kit-layer-opacity-slider"
                />
                <span class="map-kit-layer-opacity-value">{{ eumetsatInfraredOpacityPct }}%</span>
              </div>
            </div>
          </div>

          <div class="map-kit-panel-section">
            <h4 class="map-kit-panel-section-title">{{ $t('layers.overlays') }}</h4>

            <div class="map-kit-layer-control">
              <div class="map-kit-filter-checkbox-row">
                <Checkbox
                  v-model="overpassLayerVisible"
                  input-id="layer-overpasses"
                  :binary="true"
                />
                <label for="layer-overpasses">{{ $t('layers.satelliteOverpasses') }}</label>
              </div>
              <div v-if="overpassLayerVisible" class="map-kit-layer-opacity">
                <label class="map-kit-layer-opacity-label">{{ $t('layers.opacity') }}</label>
                <Slider
                  v-model="overpassOpacityPct"
                  :min="featureOpacityMinPct"
                  :max="100"
                  :step="5"
                  class="map-kit-layer-opacity-slider"
                />
                <span class="map-kit-layer-opacity-value">{{ overpassOpacityPct }}%</span>
              </div>
            </div>

            <div class="map-kit-layer-control">
              <div class="map-kit-filter-checkbox-row">
                <Checkbox
                  v-model="aoiCoverageVisible"
                  input-id="layer-aoi-coverage"
                  :binary="true"
                />
                <label for="layer-aoi-coverage">{{ $t('layers.aoiCoverage') }}</label>
              </div>
              <div v-if="aoiCoverageVisible" class="map-kit-layer-opacity">
                <label class="map-kit-layer-opacity-label">{{ $t('layers.opacity') }}</label>
                <Slider
                  v-model="aoiCoverageOpacityPct"
                  :min="featureOpacityMinPct"
                  :max="100"
                  :step="5"
                  class="map-kit-layer-opacity-slider"
                />
                <span class="map-kit-layer-opacity-value">{{ aoiCoverageOpacityPct }}%</span>
              </div>
            </div>

            <div class="map-kit-layer-control">
              <div class="map-kit-filter-checkbox-row">
                <Checkbox
                  v-model="hexGridVisible"
                  input-id="layer-hexgrid"
                  :binary="true"
                />
                <label for="layer-hexgrid">{{ $t('layers.hexGrid') }}</label>
              </div>
              <div v-if="hexGridVisible" class="map-kit-layer-opacity">
                <label class="map-kit-layer-opacity-label">{{ $t('layers.opacity') }}</label>
                <Slider
                  v-model="hexGridOpacityPct"
                  :min="hexOpacityMinPct"
                  :max="100"
                  :step="5"
                  class="map-kit-layer-opacity-slider"
                />
                <span class="map-kit-layer-opacity-value">{{ hexGridOpacityPct }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="map-kit-zoom-control">
      <Button
        icon="fas fa-plus"
        severity="contrast"
        size="small"
        text
        aria-label="Zoom in"
        @click="changeZoom(1)"
      />
      <Button
        icon="fas fa-minus"
        severity="contrast"
        size="small"
        text
        aria-label="Zoom out"
        @click="changeZoom(-1)"
      />
    </div>

    <MapMeasurement
      :show-menu="showMeasurementMenu"
      @close="showMeasurementMenu = false"
    />
  </div>

  <Teleport to="body">
    <div v-if="showGraphPrompt" class="screenshot-prompt-overlay" @click.self="onGraphPromptNo">
      <div class="screenshot-prompt-card">
        <h3 class="screenshot-prompt-title">Screenshot</h3>
        <p class="screenshot-prompt-body">
          Include the current graph/timeline view in the screenshot?
        </p>
        <div class="screenshot-prompt-actions">
          <button class="screenshot-prompt-btn screenshot-prompt-btn--primary" @click="onGraphPromptYes">Include graph</button>
          <button class="screenshot-prompt-btn screenshot-prompt-btn--secondary" @click="onGraphPromptNo">Map only</button>
          <button class="screenshot-prompt-btn screenshot-prompt-btn--ghost" @click="onGraphPromptCancel">Cancel</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { toPng } from 'html-to-image';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Slider from 'primevue/slider';

import { useFullscreen } from '@/composables/useFullscreen';
import { useMap } from '@/composables/useMap';
import { useScreenshotMode } from '@/composables/useScreenshotMode';
import {
  COLORBLIND_FEATURE_MIN_OPACITY,
  COLORBLIND_HEX_MIN_OPACITY,
  useFilterStore,
} from '@/composables/useFilterStore';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';
import MapBasemapSelector from './MapBasemapSelector.vue';
import MapMeasurement from './MapMeasurement.vue';

const { map } = useMap();
const { isFullscreen, toggleFullscreen } = useFullscreen();
const { screenshotMode } = useScreenshotMode();
const {
  overpassLayerVisible,
  overpassLayerOpacity,
  eumetsatTrueRgbVisible,
  eumetsatTrueRgbOpacity,
  eumetsatInfraredVisible,
  eumetsatInfraredOpacity,
  hexGridVisible,
  hexGridOpacity,
  aoiCoverageVisible,
  aoiCoverageOpacity,
} = useFilterStore();

const controlContainer = ref<HTMLElement | null>(null);
const layersMenuAnchor = ref<HTMLElement | null>(null);
const layersDrawer = ref<HTMLElement | null>(null);
const showMeasurementMenu = ref(false);
const showGraphPrompt = ref(false);
const showLayersMenu = ref(false);
const layersDrawerMaxHeight = ref('40rem');
const layersDrawerMaxWidth = ref('21rem');
const isLayersDrawerCompact = ref(false);
let includeGraph = false;
let layersDrawerResizeObserver: ResizeObserver | null = null;
let observedLayersDrawerElements: HTMLElement[] = [];

const featureOpacityMinPct = computed(() => (
  isColorblindFriendly.value ? COLORBLIND_FEATURE_MIN_OPACITY * 100 : 0
));
const hexOpacityMinPct = computed(() => (
  isColorblindFriendly.value ? COLORBLIND_HEX_MIN_OPACITY * 100 : 0
));

const layersDrawerStyle = computed<Record<string, string>>(() => ({
  '--mk-layer-drawer-max-height': layersDrawerMaxHeight.value,
  '--mk-layer-drawer-max-width': layersDrawerMaxWidth.value,
}));

const overpassOpacityPct = computed({
  get: () => Math.round(overpassLayerOpacity.value * 100),
  set: (value: number) => { overpassLayerOpacity.value = value / 100; },
});
const eumetsatTrueRgbOpacityPct = computed({
  get: () => Math.round(eumetsatTrueRgbOpacity.value * 100),
  set: (value: number) => { eumetsatTrueRgbOpacity.value = value / 100; },
});
const eumetsatInfraredOpacityPct = computed({
  get: () => Math.round(eumetsatInfraredOpacity.value * 100),
  set: (value: number) => { eumetsatInfraredOpacity.value = value / 100; },
});
const hexGridOpacityPct = computed({
  get: () => Math.round(hexGridOpacity.value * 100),
  set: (value: number) => { hexGridOpacity.value = value / 100; },
});
const aoiCoverageOpacityPct = computed({
  get: () => Math.round(aoiCoverageOpacity.value * 100),
  set: (value: number) => { aoiCoverageOpacity.value = value / 100; },
});

watch(showLayersMenu, (open) => {
  if (!open) return;

  showMeasurementMenu.value = false;
  nextTick(() => {
    syncLayersDrawerObservers();
    updateLayersDrawerLayout();
    requestAnimationFrame(() => {
      updateLayersDrawerLayout();
      layersDrawer.value?.focus();
    });
  });
});

watch(showMeasurementMenu, (open) => {
  if (open) showLayersMenu.value = false;
});

function toggleLayersMenu() {
  showLayersMenu.value = !showLayersMenu.value;
}

function getLayersDrawerSurface(): HTMLElement | null {
  return controlContainer.value?.closest('.map-kit-map') as HTMLElement | null;
}

function updateLayersDrawerLayout() {
  if (!showLayersMenu.value) return;

  const anchorRect = layersMenuAnchor.value?.getBoundingClientRect();
  const surfaceRect = getLayersDrawerSurface()?.getBoundingClientRect();
  if (!anchorRect || !surfaceRect) return;

  const safeInset = 12;
  const availableHeight = Math.max(0, Math.floor(anchorRect.bottom - surfaceRect.top - safeInset));
  const availableWidth = Math.max(0, Math.floor(anchorRect.left - surfaceRect.left - safeInset));

  layersDrawerMaxHeight.value = `${availableHeight}px`;
  layersDrawerMaxWidth.value = `${availableWidth}px`;
  isLayersDrawerCompact.value = availableHeight < 360 || availableWidth < 300;
}

function syncLayersDrawerObservers() {
  if (typeof ResizeObserver === 'undefined') return;

  if (!layersDrawerResizeObserver) {
    layersDrawerResizeObserver = new ResizeObserver(() => {
      updateLayersDrawerLayout();
    });
  }

  const nextElements = [layersMenuAnchor.value, getLayersDrawerSurface()].filter(
    (value): value is HTMLElement => value instanceof HTMLElement,
  );

  for (const element of observedLayersDrawerElements) {
    if (!nextElements.includes(element)) {
      layersDrawerResizeObserver.unobserve(element);
    }
  }

  for (const element of nextElements) {
    if (!observedLayersDrawerElements.includes(element)) {
      layersDrawerResizeObserver.observe(element);
    }
  }

  observedLayersDrawerElements = nextElements;
}

function onDocumentClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  if (controlContainer.value?.contains(target)) return;
  showLayersMenu.value = false;
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  if (showLayersMenu.value) {
    showLayersMenu.value = false;
    return;
  }
  if (showMeasurementMenu.value) {
    showMeasurementMenu.value = false;
  }
}

function onViewportResize() {
  updateLayersDrawerLayout();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
  window.addEventListener('resize', onViewportResize);
  window.visualViewport?.addEventListener('resize', onViewportResize);
  syncLayersDrawerObservers();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
  window.removeEventListener('resize', onViewportResize);
  window.visualViewport?.removeEventListener('resize', onViewportResize);
  layersDrawerResizeObserver?.disconnect();
  layersDrawerResizeObserver = null;
  observedLayersDrawerElements = [];
});

const changeZoom = (delta: number) => {
  const view = map.value?.getView();
  if (!view) return;
  const zoom = view.getZoom();
  if (zoom == null) return;
  view.animate({ zoom: zoom + delta, duration: 250 });
};

function onScreenshotClick() {
  showGraphPrompt.value = true;
}

function onGraphPromptYes() {
  includeGraph = true;
  showGraphPrompt.value = false;
  startScreenshotCapture();
}

function onGraphPromptNo() {
  includeGraph = false;
  showGraphPrompt.value = false;
  startScreenshotCapture();
}

function onGraphPromptCancel() {
  showGraphPrompt.value = false;
}

async function startScreenshotCapture() {
  screenshotMode.value = true;
  await nextTick();
  await new Promise((r) => setTimeout(r, 300));

  try {
    const mapEl = document.getElementById('demo-map');
    if (!mapEl) return;

    const mapDataUrl = await toPng(mapEl, {
      cacheBust: true,
      pixelRatio: 2,
      filter: (node: HTMLElement) => {
        if (node.classList?.contains('map-kit-zoom-hint')) return false;
        if (node.classList?.contains('map-kit-popup')) return false;
        return true;
      },
    });

    if (includeGraph) {
      const graphEl = document.querySelector<HTMLElement>('.map-kit-bottom-panel');
      if (graphEl && graphEl.offsetHeight > 0) {
        const graphDataUrl = await toPng(graphEl, {
          cacheBust: true,
          pixelRatio: 2,
        });

        const combined = await combineImages(mapDataUrl, graphDataUrl);
        downloadImage(combined, 'map-screenshot.png');
      } else {
        downloadImage(mapDataUrl, 'map-screenshot.png');
      }
    } else {
      downloadImage(mapDataUrl, 'map-screenshot.png');
    }
  } catch (err) {
    console.error('Screenshot capture failed:', err);
  } finally {
    screenshotMode.value = false;
  }
}

function combineImages(topDataUrl: string, bottomDataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const topImg = new Image();
    const bottomImg = new Image();
    let loaded = 0;

    const onLoad = () => {
      loaded++;
      if (loaded < 2) return;

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(topImg.width, bottomImg.width);
      canvas.height = topImg.height + bottomImg.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('No canvas context'));

      ctx.fillStyle = '#0C232B';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(topImg, 0, 0);
      ctx.drawImage(bottomImg, 0, topImg.height);

      resolve(canvas.toDataURL('image/png'));
    };

    topImg.onload = onLoad;
    bottomImg.onload = onLoad;
    topImg.onerror = reject;
    bottomImg.onerror = reject;
    topImg.src = topDataUrl;
    bottomImg.src = bottomDataUrl;
  });
}

function downloadImage(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  link.click();
}
</script>

<style scoped lang="scss">
.map-kit-interaction-buttons {
  position: relative;
}

.map-kit-layer-drawer {
  --mk-layer-drawer-gap: 0.55rem;
  position: absolute;
  right: calc(100% + var(--mk-layer-drawer-gap));
  bottom: 0;
  z-index: 2;
  width: min(21rem, var(--mk-layer-drawer-max-width, 21rem));
  max-height: min(var(--mk-layer-drawer-max-height, 40rem), 40rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 14px;
  background: rgba(12, 35, 43, 0.96);
  border: 1px solid rgba(250, 249, 247, 0.12);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(10px);
  color: var(--p-navbar-color, #ffffffcc);

  &:focus-visible {
    outline: 2px solid rgba(3, 222, 204, 0.65);
    outline-offset: 2px;
  }
}

.map-kit-layer-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 0.95rem 0.65rem;
  border-bottom: 1px solid rgba(250, 249, 247, 0.08);
}

.map-kit-layer-drawer-title {
  margin: 0;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--p-text-muted-color, #8DA0A7);
}

.map-kit-layer-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
  padding: 0.9rem 0.95rem 0.8rem;
}

.map-kit-layer-drawer-body .map-kit-panel-section {
  margin: 0;
}

.map-kit-layer-drawer-body .map-kit-panel-section + .map-kit-panel-section {
  padding-top: 0.2rem;
}

.map-kit-layer-drawer-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 999px;
  border: 1px solid rgba(250, 249, 247, 0.12);
  background: rgba(250, 249, 247, 0.04);
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(250, 249, 247, 0.08);
  }
}

.map-kit-layer-drawer .map-kit-filter-checkbox-row {
  align-items: flex-start;
}

.map-kit-layer-drawer .map-kit-filter-checkbox-row label {
  flex: 1;
  min-width: 0;
  line-height: 1.35;
}

.map-kit-layer-drawer :deep(.map-kit-basemap-option) {
  border-radius: 8px;
}

.map-kit-layer-drawer--compact .map-kit-layer-drawer-header {
  padding: 0.8rem 0.85rem 0.55rem;
}

.map-kit-layer-drawer--compact .map-kit-layer-drawer-body {
  gap: 0.75rem;
  padding: 0.75rem 0.85rem 0.7rem;
}

.map-kit-layer-drawer--compact .map-kit-layer-opacity {
  flex-wrap: wrap;
  row-gap: 0.35rem;
}

.map-kit-layer-drawer--compact .map-kit-layer-opacity-label {
  min-width: 100%;
}

.map-kit-layer-drawer--compact .map-kit-layer-opacity-value {
  min-width: auto;
}

@media (max-width: 900px) {
  .map-kit-layer-drawer {
    width: min(19rem, var(--mk-layer-drawer-max-width, 19rem));
  }
}

@media (max-width: 640px) {
  .map-kit-layer-drawer {
    --mk-layer-drawer-gap: 0.35rem;
  }
}

:root:not(.color-scheme-dark) .map-kit-layer-drawer {
  background: rgba(255, 255, 255, 0.98);
  border-color: rgba(15, 23, 42, 0.12);
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.16);
  color: #0f172a;
}

:root:not(.color-scheme-dark) .map-kit-layer-drawer-close {
  border-color: rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.04);

  &:hover {
    background: rgba(15, 23, 42, 0.08);
  }
}

:root:not(.color-scheme-dark) .map-kit-layer-drawer-header {
  border-bottom-color: rgba(15, 23, 42, 0.08);
}
</style>
